// ДДС разбивка за фактури — нето / ДДС / бруто, гарантирано реконсилиране.
//
// Правилото (1:1 с BE, app/features/invoices/service.py):
//
// 1. Бруто на реда (с ДДС) е истината — реалните пари. Тоталът = Σ бруто.
// 2. За всяка ДДС ставка (група):
//      ДДС_група = round_half_up(бруто_група * ставка / (100 + ставка))
//      нето_група = бруто_група − ДДС_група        (нето е остатъкът)
//    → при половин стотинка ДДС-то отива нагоре, нетото надолу (счет. норма).
// 3. Нетото на групата се РАЗПРЕДЕЛЯ по редовете (largest remainder):
//      floor на всяко нето; остатъчните центове отиват на редовете с
//      най-голям остатък (при равенство → по-горния ред).
//      ДДС на ред = бруто_ред − нето_ред.
//
// Така Σ нето_редове = нето_група и Σ ДДС_редове = ДДС_група — ВИНАГИ.
// Цялата аритметика е в целочислени стотинки → няма float грешки и съвпада
// точно с Decimal(ROUND_HALF_UP) на бекенда.
//
// 4. Редът за бакшиш БЕЗ ДДС (`kind: "tip"` със ставка 0 — група А, 1:1 с BE
//    `invoices/service.py` + `tip_lines.py`) НЕ влиза в нито една ДДС група,
//    нито в „Необлагаема сума" (`nonTaxable`), нито в `totalSum`: той не е
//    доставка с 0 % ставка. Събира се отделно в `tipTotal`, а
//      grandTotal = totalSum + vatTotal + tipTotal.
//    Облагаемият бакшиш (`kind: "tip"` със ставка > 0 — група Б/В) е обикновен
//    облагаем ред: влиза в групата на ставката си и НЕ е в `tipTotal`.
//    Без редове за бакшиш всеки резултат е точно досегашният (`tipTotal` = 0).

export interface VatLineInput {
  gross: number; // сума с ДДС на реда (количество × ед. цена с ДДС)
  rate: number; // ДДС ставка в проценти (0 / 9 / 20)
  /** `tip` — записаният бакшиш на сметка: със ставка 0 — извън ДДС групите,
   *  със ставка > 0 — облагаем ред като всеки друг (виж т. 4). */
  kind?: "item" | "tip";
}

export interface VatLineBreakdown {
  net: number; // нето (без ДДС)
  vat: number; // ДДС
  gross: number; // бруто (с ДДС)
}

export interface VatGroup {
  rate: number;
  net: number;
  vat: number;
  gross: number;
}

export interface VatBreakdown {
  lines: VatLineBreakdown[]; // подравнено с входа по индекс
  groups: VatGroup[]; // ставки > 0, възходящо
  taxable: number; // Σ нето на облагаемите редове
  nonTaxable: number; // Σ бруто на редовете със ставка 0
  vatTotal: number;
  totalSum: number; // taxable + nonTaxable (без ДДС; без бакшиша)
  tipTotal: number; // Σ редовете за бакшиш БЕЗ ДДС; 0 без такъв бакшиш
  grandTotal: number; // totalSum + vatTotal + tipTotal == Σ бруто
}

/** Бруто в цели стотинки (устойчиво на float). */
function toCents(value: number): number {
  return Math.round((Number.isFinite(value) ? value : 0) * 100);
}

/** round_half_up(N / D) за положителни цели числа, без float грешки. */
function divRoundHalfUp(numerator: number, denominator: number): number {
  return Math.floor((2 * numerator + denominator) / (2 * denominator));
}

/** Нето ед. цена (без ДДС), независимо закръглена half-up — информативна колона. */
export function netUnitPrice(grossUnitPrice: number, ratePercent: number): number {
  if (ratePercent <= 0) return Math.round(grossUnitPrice * 100) / 100;
  const cents = toCents(grossUnitPrice);
  return divRoundHalfUp(cents * 100, 100 + ratePercent) / 100;
}

/** Ред за бакшиш без ДДС (група А) — единственият извън ДДС групите. */
export function isNoVatTipInput(line: Pick<VatLineInput, "kind" | "rate">): boolean {
  return line.kind === "tip" && !(line.rate > 0);
}

/** Пълна ДДС разбивка с реконсилиране до стотинка. */
export function computeVatBreakdown(input: VatLineInput[]): VatBreakdown {
  const lines: VatLineBreakdown[] = input.map((l) => {
    const gc = toCents(l.gross);
    return { net: 0, vat: 0, gross: gc / 100 };
  });

  // Групиране по ставка, със запазен ред на оригиналните индекси. Редовете
  // за бакшиш без ДДС стоят извън групите: нето = бруто, ДДС = 0, сумата им →
  // tipTotal. Облагаемият бакшиш (ставка > 0) се групира като всеки ред.
  const byRate = new Map<number, number[]>();
  let tipCents = 0;
  input.forEach((l, i) => {
    if (isNoVatTipInput(l)) {
      const tipLineCents = toCents(l.gross);
      lines[i].net = tipLineCents / 100;
      lines[i].vat = 0;
      lines[i].gross = tipLineCents / 100;
      tipCents += tipLineCents;
      return;
    }
    const arr = byRate.get(l.rate);
    if (arr) arr.push(i);
    else byRate.set(l.rate, [i]);
  });

  const groups: VatGroup[] = [];
  let taxable = 0;
  let nonTaxable = 0;
  let vatTotal = 0;
  // Същите суми в цели стотинки — за `grandTotal` при бакшиш (виж долу).
  let productCents = 0;

  const rates = Array.from(byRate.keys()).sort((a, b) => a - b);
  for (const rate of rates) {
    const idxs = byRate.get(rate)!;
    const grossCents = idxs.map((i) => toCents(input[i].gross));
    const groupGross = grossCents.reduce((a, b) => a + b, 0);

    if (rate <= 0) {
      // Необлагаемо: нето = бруто, ДДС = 0.
      idxs.forEach((i, k) => {
        lines[i].net = grossCents[k] / 100;
        lines[i].vat = 0;
        lines[i].gross = grossCents[k] / 100;
      });
      nonTaxable += groupGross / 100;
      productCents += groupGross;
      groups.push({ rate, net: groupGross / 100, vat: 0, gross: groupGross / 100 });
      continue;
    }

    // Анкер: ДДС на групата (half-up), нето = бруто − ДДС.
    const groupVat = divRoundHalfUp(groupGross * rate, 100 + rate);
    const groupNet = groupGross - groupVat;

    // Разпределяне на нетото по редовете: floor + остатъчни центове.
    const D = 100 + rate;
    const floors = grossCents.map((gc) => Math.floor((gc * 100) / D));
    const remainders = grossCents.map((gc) => (gc * 100) % D);
    const sumFloors = floors.reduce((a, b) => a + b, 0);
    const leftover = groupNet - sumFloors;

    // Подреждаме редовете по най-голям остатък, после по позиция в групата.
    const order = idxs
      .map((_, k) => k)
      .sort((a, b) => remainders[b] - remainders[a] || a - b);
    const netCents = floors.slice();
    for (let k = 0; k < leftover; k++) netCents[order[k]] += 1;

    idxs.forEach((i, k) => {
      lines[i].net = netCents[k] / 100;
      lines[i].vat = (grossCents[k] - netCents[k]) / 100;
      lines[i].gross = grossCents[k] / 100;
    });
    taxable += groupNet / 100;
    vatTotal += groupVat / 100;
    productCents += groupGross; // = нето + ДДС на групата
    groups.push({
      rate,
      net: groupNet / 100,
      vat: groupVat / 100,
      gross: groupGross / 100,
    });
  }

  const totalSum = taxable + nonTaxable;
  const tipTotal = tipCents / 100;
  // Без бакшиш — ТОЧНО досегашният израз (същото float число). С бакшиш —
  // от целите стотинки: още едно float събиране дава напр. 199.99999999999997
  // вместо 200, а „Словом" (`moneyToWordsBg`, floor на еврото) би казал
  // „сто деветдесет и девет евро и сто цента".
  const grandTotal =
    tipCents === 0 ? totalSum + vatTotal : (productCents + tipCents) / 100;
  return { lines, groups, taxable, nonTaxable, vatTotal, totalSum, tipTotal, grandTotal };
}
